/* eslint-disable no-console -- token refresh diagnostics */
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, Observable, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { API_CONSTANTS } from '../constants/api.constants';

/**
 * HTTP Interceptor to handle expired access tokens and refresh flow.
 *
 * If a request fails with 401 (unauthorized), this interceptor attempts to refresh
 * the access token using a stored cookie (HttpOnly). If the refresh is successful,
 * the original request is retried. If not, the user is redirected to the login page.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshToken$?: Observable<unknown>;

    constructor(private readonly authService: AuthApiService) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((err: HttpErrorResponse) => {
                // Only handle 401 (Unauthorized)
                if (err.status === 401 && !request.url.includes(`${API_CONSTANTS.ENDPOINTS.AUTH}/refresh`)) {
                    return this.handle401(request, next);
                }

                return throwError(() => err);
            })
        );
    }

    /**
     * Handles 401 errors by attempting token refresh.
     */
    private handle401(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;

            this.refreshToken$ = this.authService.refresh().pipe(
                tap(() => console.info('[AuthInterceptor] Token successfully refreshed')),
                catchError((refreshError) => {
                    console.warn('[AuthInterceptor] Refresh failed, logging out');
                    this.authService.logout();
                    return throwError(() => refreshError);
                }),
                finalize(() => {
                    this.isRefreshing = false;
                    this.refreshToken$ = undefined; // Clear for next attempt
                }),
                shareReplay(1)
            );
        }

        return this.refreshToken$!.pipe(
            take(1),
            switchMap(() => next.handle(request))
        );
    }
}
